import { c as _c } from "react/compiler-runtime";
import { IEBridgeWordmark } from "@/components/shell/IEBridgeWordmark";
import { Card } from "@/components/ui/Card";
import { focusRingClassName } from "@/components/ui/control-surface";
import { Heading } from "@/components/ui/Heading";
import { Spinner } from "@/components/ui/Spinner";
import { Check, ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import * as React from "react";
import cn from "twsx";

// The page template for everything outside the signed-in app: sign-in,
// workspace switching, shared links and public signing. A 56px brand bar sits
// above one centred column on the panel background. `auth` is Linear's 360px
// login column; the other widths hold shared documents.

const publicPageWidths = {
  auth: "max-w-auth pt-16 md:pt-24",
  md: "max-w-3xl pt-6",
  lg: "max-w-6xl pt-6",
  xl: "max-w-7xl pt-6"
} as const;
export type PublicPageProps = {
  children: React.ReactNode;
  homeHref?: string;
  width?: keyof typeof publicPageWidths;
};
export function PublicPage(t0) {
  const $ = _c(11);
  const {
    children,
    homeHref,
    width: t1
  } = t0;
  const width = t1 === undefined ? "md" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <IEBridgeWordmark className="text-xl" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const wordmark = t2;
  let t3;
  if ($[1] !== homeHref) {
    t3 = <header className="flex h-14 shrink-0 items-center px-6">{homeHref ? <Link href={homeHref} aria-label="IEBridge home" className={cn(focusRingClassName, "rounded-kbd")}>{wordmark}</Link> : wordmark}</header>;
    $[1] = homeHref;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = publicPageWidths[width];
  let t5;
  if ($[3] !== t4) {
    t5 = cn("mx-auto flex w-full flex-1 flex-col gap-6 px-5 pb-10", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children || $[6] !== t5) {
    t6 = <main className={t5}>{children}</main>;
    $[5] = children;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <div className="flex min-h-svh flex-col bg-bg-primary text-text-primary">{t3}{t6}</div>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
export function PublicPageHeader(t0) {
  const $ = _c(16);
  const {
    actions,
    eyebrow,
    icon: Icon,
    meta,
    title
  } = t0;
  let t1;
  if ($[0] !== Icon || $[1] !== eyebrow) {
    t1 = eyebrow && <p className="flex items-center gap-1.5 text-base font-medium leading-5 text-text-tertiary">{Icon && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}{eyebrow}</p>;
    $[0] = Icon;
    $[1] = eyebrow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== title) {
    t2 = <Heading level={1}>{title}</Heading>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== meta) {
    t3 = meta && <p className="text-base leading-5 text-text-tertiary">{meta}</p>;
    $[5] = meta;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="flex min-w-0 flex-col gap-1">{t1}{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== actions) {
    t5 = actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>;
    $[11] = actions;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <header className="flex flex-wrap items-end justify-between gap-4">{t4}{t5}</header>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

// Numbered progress for multi-step public flows.
export function PublicPageSteps(t0) {
  const $ = _c(7);
  const {
    label,
    steps
  } = t0;
  let t1;
  if ($[0] !== steps) {
    t1 = steps.map(_temp);
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <ol className="flex items-center gap-3 text-base leading-5 text-text-tertiary">{t1}</ol>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== t2) {
    t3 = <nav aria-label={label}>{t2}</nav>;
    $[4] = label;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// A shared document rendered in a viewport-tall framed iframe.
function _temp(step, index) {
  return <li key={step.label} className="flex items-center gap-3">{index > 0 && <span aria-hidden="true">/</span>}<span aria-current={step.current ? "step" : undefined} className={cn(step.current && "font-medium text-text-primary")}>{index + 1}. {step.label}</span></li>;
}
export function PublicPageFrame(t0) {
  const $ = _c(3);
  const {
    src,
    title
  } = t0;
  let t1;
  if ($[0] !== src || $[1] !== title) {
    t1 = <Card height="viewport"><iframe className="size-full border-0 bg-bg-surface" src={src} title={title} /></Card>;
    $[0] = src;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// A document beside a sticky 360px panel. On small screens the panel stacks
// below the document, or above it with `asideFirst`.
export function PublicPageSplit(t0) {
  const $ = _c(13);
  const {
    aside,
    asideFirst: t1,
    children
  } = t0;
  const asideFirst = t1 === undefined ? false : t1;
  const t2 = asideFirst && "order-2";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex h-frame-stacked min-h-80 flex-col lg:order-none lg:h-frame", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = asideFirst && "order-1";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("flex flex-col self-start divide-y divide-border-subtle overflow-hidden lg:sticky lg:top-6 lg:order-none", "rounded-settings-list-view-item border border-border-primary bg-bg-surface", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== aside || $[8] !== t6) {
    t7 = <aside className={t6}>{aside}</aside>;
    $[7] = aside;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t7) {
    t8 = <div className="grid min-h-0 gap-6 lg:grid-cols-main-panel">{t4}{t7}</div>;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}

// One block of the split panel; blocks divide on a hairline.
export function PublicPageSection(t0) {
  const $ = _c(5);
  const {
    children,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "default" : t1;
  const t2 = tone === "muted" && "text-text-tertiary";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex flex-col gap-4 p-4 text-base leading-5", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

// A note under the auth column, such as the legal line under sign-in.
export function PublicPageFootnote(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <p className="text-xs leading-4 text-text-tertiary [&_a]:font-medium [&_a]:text-text-primary [&_a:hover]:underline">{children}</p>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function PublicPageOptionList(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="-mx-3 flex flex-col gap-1">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// A selectable row in the auth column (workspace picker). The trailing mark
// shows the current choice, a pending choice, or a chevron.
export function PublicPageOption(t0) {
  const $ = _c(16);
  const {
    description,
    disabled,
    leading,
    onSelect,
    state,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(focusRingClassName, "flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-left hover:bg-fill-hover", "disabled:pointer-events-none disabled:opacity-disabled");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <span className="truncate text-base font-medium leading-5">{title}</span>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== description) {
    t3 = description && <span className="truncate text-xs leading-4 text-text-tertiary">{description}</span>;
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <span className="flex min-w-0 flex-1 flex-col">{t2}{t3}</span>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== state) {
    t5 = state === "pending" ? <Spinner size="md" tone="muted" /> : state === "active" ? <Check className="size-4 shrink-0 text-accent" aria-hidden="true" /> : <ChevronRight className="size-4 shrink-0 text-text-quaternary" aria-hidden="true" />;
    $[8] = state;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== disabled || $[11] !== leading || $[12] !== onSelect || $[13] !== t4 || $[14] !== t5) {
    t6 = <button type="button" disabled={disabled} onClick={onSelect} className={t1}>{leading}{t4}{t5}</button>;
    $[10] = disabled;
    $[11] = leading;
    $[12] = onSelect;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}